// src/components/FrontOfficeComp/LeftPanelNew.js
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { ASTERISK_API_BASE } from '../../config/bridge';
import { buildTransferAgentQuery, excludeCurrentAgentEntries } from '../../lib/transferAgentIdentity';

const LeftPanelNew = forwardRef(({
  isLive,
  onDialpadOpen,
  onHangup,
  onEmergencyHangup,
  onTransferOpen,
  onTransferComplete,
  onAttendedTransferComplete,
  onSearchHistoryOpen,
  onListOpen,
  onVoicemailOpen,
  onProfileOpen,
  onReminderOpen,
  onViewHistoryOpen,
  onNoteOpen,
  onSmsOpen,
  onVideoChatOpen,
  onSIPRegistrationOpen,
  asteriskConnected = false,
  currentChannel = null,
  isCallActive = false,
  isMuted = false,
  onToggleMute = null,
  currentUser = null,
  callerId = '',
  queryId = '',
  queueMetrics = null,
  recordingState = null,
  canUseDialpad = true,
  onSoftphoneHoldChange = null,
  incomingCallContent = null,
  showStandaloneHangup = false,
  hideStandaloneHangup = false
}, ref) => {
  const [isOnHold, setIsOnHold] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [activeAttendedTransfer, setActiveAttendedTransfer] = useState(null);
  const [transferringToFeedback, setTransferringToFeedback] = useState(false);
  const [transferSuccess, setTransferSuccess] = useState(null);
  const [holding, setHolding] = useState(false);
  const [queueDurationTick, setQueueDurationTick] = useState(0);

  const resolveChannelId = useCallback(() => {
    if (!currentChannel) return '';
    if (typeof currentChannel === 'string') return currentChannel;
    return currentChannel.id || currentChannel.channelId || '';
  }, [currentChannel]);

  const toggleHold = useCallback(async () => {
    const channelId = resolveChannelId();
    if (!channelId && !onSoftphoneHoldChange) {
      alert('No active call to hold/unhold');
      return;
    }

    setHolding(true);

    try {
      if (onSoftphoneHoldChange) {
        const changed = await Promise.resolve(onSoftphoneHoldChange(!isOnHold));
        if (changed === false) {
          alert(`Failed to ${isOnHold ? 'unhold' : 'hold'} the SIP call`);
          return;
        }

        setIsOnHold((prev) => !prev);
        return;
      }

      let response;
      if (isOnHold) {
        // Unhold the call
        response = await fetch(`${ASTERISK_API_BASE}/unhold`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ channelId })
        });
      } else {
        // Hold the call
        response = await fetch(`${ASTERISK_API_BASE}/hold`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ channelId })
        });
      }

      const result = await response.json();

      if (response.ok) {
        // Toggle the hold state
        setIsOnHold(!isOnHold);
        alert(`Call ${isOnHold ? 'resumed' : 'placed'} on hold successfully`);
      } else {
        alert(`Failed to ${isOnHold ? 'unhold' : 'hold'} call: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Hold/Unhold error:', error);
      alert(`Network error occurred while trying to ${isOnHold ? 'unhold' : 'hold'} the call`);
    } finally {
      setHolding(false);
    }
  }, [isOnHold, onSoftphoneHoldChange, resolveChannelId]);

  useEffect(() => {
    if (!isLive) {
      setIsOnHold(false);
    }
  }, [isLive]);

  useEffect(() => {
    if (!showTransferModal || !isCallActive) {
      if (!isCallActive) {
        setShowTransferModal(false);
        setActiveAttendedTransfer(null);
        setLoadingUsers(false);
      }
      return undefined;
    }

    let cancelled = false;
    let refreshTimeoutId;
    let controller;

    const fetchUsers = async (initial = false) => {
      controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(`${ASTERISK_API_BASE}/transfer/agents${buildTransferAgentQuery()}`, {
          signal: controller.signal,
          cache: 'no-store'
        });
        if (!response.ok) throw new Error('Failed to load transfer agents');
        const data = await response.json();
        if (!cancelled) {
          setUsers(excludeCurrentAgentEntries(data.agents || []));
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching transfer agents:', error);
        if (initial) {
          setTransferSuccess({
            success: false,
            message: error?.name === 'AbortError' ? 'Transfer agent list timed out. Try again.' : 'Failed to load transfer agents.'
          });
        }
        setUsers([]);
      } finally {
        window.clearTimeout(timeoutId);
        if (!cancelled) {
          setLoadingUsers(false);
          refreshTimeoutId = window.setTimeout(() => fetchUsers(), 2000);
        }
      }
    };

    fetchUsers(true);
    return () => {
      cancelled = true;
      window.clearTimeout(refreshTimeoutId);
      controller?.abort();
    };
  }, [isCallActive, showTransferModal]);

  const handleTransferClick = useCallback(() => {
    if (!isCallActive) return false;
    if (showTransferModal) return true;
    setLoadingUsers(true);
    setTransferSuccess(null);
    setUsers([]);
    setShowTransferModal(true);
    if (onTransferOpen) {
      onTransferOpen();
    }
    return true;
  }, [isCallActive, onTransferOpen, showTransferModal]);

  const handleFeedbackTransfer = useCallback(async () => {
    const channelId = resolveChannelId();
    if (!channelId) {
      alert('No active call to send to feedback');
      return;
    }

    setTransferringToFeedback(true);

    try {
      const response = await fetch(`${ASTERISK_API_BASE}/transfer-to-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channelId,
          callerId: callerId || '',
          queryId: queryId || '',
          agentExtension: currentUser?.extension || ''
        })
      });

      const result = await response.json();

      if (response.ok) {
        alert(result.message || 'Call routed to feedback successfully');
        if (onTransferComplete) {
          onTransferComplete();
        }
      } else {
        alert(result.error || 'Failed to route call to feedback');
      }
    } catch (error) {
      console.error('Feedback transfer error:', error);
      alert('Network error occurred during feedback transfer');
    } finally {
      setTransferringToFeedback(false);
    }
  }, [callerId, currentUser?.extension, onTransferComplete, queryId, resolveChannelId]);

  const handleUserTransfer = useCallback(async (targetExtension, displayName) => {
    const channelId = resolveChannelId();
    if (!isCallActive || !channelId) {
      alert('No active call to transfer');
      return;
    }

    if (!window.confirm(`Are you sure you want to transfer the current call to ${displayName} (ext: ${targetExtension})?`)) {
      return;
    }

    setTransferring(true);
    setTransferSuccess(null);

    try {
      const response = await fetch(`${ASTERISK_API_BASE}/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channelId,
          targetExtension: targetExtension,
          callerId: callerId || '',
          agentExtension: currentUser?.extension || ''
        })
      });

      const result = await response.json();

      if (response.ok) {
        setTransferSuccess({ success: true, message: result.message || `Transfer initiated to ${displayName}` });
        if (result.transferInfo?.mode === 'ami-attended' && result.transferInfo?.active !== false) {
          setActiveAttendedTransfer(result.transferInfo);
        } else {
          setTimeout(() => {
            setShowTransferModal(false);
          }, 2000);
        }
      } else {
        setTransferSuccess({
          success: false,
          message: result.detail ? `${result.error || 'Transfer failed'}: ${result.detail}` : (result.error || 'Transfer failed')
        });
      }
    } catch (error) {
      console.error('Transfer error:', error);
      setTransferSuccess({ success: false, message: 'Network error occurred during transfer' });
    } finally {
      setTransferring(false);
    }
  }, [callerId, currentUser?.extension, isCallActive, resolveChannelId]);

  const handleAttendedTransferAction = useCallback(async (action) => {
    if (!activeAttendedTransfer?.channelId) return;

    setTransferring(true);
    setTransferSuccess(null);
    try {
      const response = await fetch(`${ASTERISK_API_BASE}/transfer/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channelId: activeAttendedTransfer.channelId,
          agentExtension: currentUser?.extension || ''
        })
      });
      const result = await response.json();

      if (!response.ok) {
        setTransferSuccess({ success: false, message: result.error || `Failed to ${action} transfer` });
        return;
      }

      setTransferSuccess({ success: true, message: result.message });
      if (action === 'complete' && onAttendedTransferComplete) {
        await Promise.resolve(onAttendedTransferComplete());
      }
      setActiveAttendedTransfer(null);
      setTimeout(() => setShowTransferModal(false), 1200);
    } catch (error) {
      console.error(`Attended transfer ${action} error:`, error);
      setTransferSuccess({ success: false, message: `Network error occurred while trying to ${action} the transfer` });
    } finally {
      setTransferring(false);
    }
  }, [activeAttendedTransfer, currentUser?.extension, onAttendedTransferComplete]);

  const statusText = useMemo(() => {
    if (!asteriskConnected) return "Asterisk Offline";
    if (isOnHold) return "On Hold";
    if (isMuted) return "Muted";
    if (isLive) return "Connected";
    return "Ready";
  }, [asteriskConnected, isLive, isMuted, isOnHold]);

  const statusColor = useMemo(() => {
    if (!asteriskConnected) return "bg-danger";
    if (isOnHold) return "bg-warning";
    if (isMuted) return "bg-warning";
    if (isLive) return "bg-success";
    return "bg-secondary";
  }, [asteriskConnected, isLive, isMuted, isOnHold]);

  const getUserStatusClass = useCallback((status) => {
    const normalizedStatus = String(status || '').toLowerCase();
    if (normalizedStatus === 'ready' || normalizedStatus === 'idle' || normalizedStatus === 'registered') {
      return 'bg-success';
    } else if (normalizedStatus === 'not ready' || normalizedStatus === 'notready') {
      return 'bg-danger';
    } else if (normalizedStatus === 'oncall' || normalizedStatus === 'on call') {
      return 'bg-warning';
    } else if (
      normalizedStatus === 'lunch' ||
      normalizedStatus === 'training' ||
      normalizedStatus === 'meeting' ||
      normalizedStatus === 'wrapup' ||
      normalizedStatus === 'wrap-up'
    ) {
      return 'bg-info';
    }
    return 'bg-secondary';
  }, []);

  const getUserStatusDisplay = useCallback((status) => {
    const normalizedStatus = String(status || '').toLowerCase();
    if (normalizedStatus === 'ready' || normalizedStatus === 'idle') {
      return 'Ready';
    } else if (normalizedStatus === 'not ready' || normalizedStatus === 'notready') {
      return 'Not Ready';
    } else if (normalizedStatus === 'oncall' || normalizedStatus === 'on call') {
      return 'On Call';
    } else if (normalizedStatus === 'lunch') {
      return 'Lunch';
    } else if (normalizedStatus === 'training') {
      return 'Training';
    } else if (normalizedStatus === 'meeting') {
      return 'Meeting';
    } else if (normalizedStatus === 'wrapup' || normalizedStatus === 'wrap-up') {
      return 'Wrap-up';
    }
    return status;
  }, []);

  const isTransferableStatus = useCallback((status) => {
    const normalizedStatus = String(status || '').toLowerCase();
    return normalizedStatus === 'ready' || normalizedStatus === 'idle';
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setQueueDurationTick((tick) => tick + 1);
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);

  const hasCallerId = Boolean(String(callerId || '').trim());
  const hasQueryId = Boolean(String(queryId || '').trim());
  const formatDuration = useCallback((seconds) => {
    const totalSeconds = Math.max(0, Number(seconds || 0));
    const mins = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const secs = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }, []);
  const waitingCallers = Math.max(0, Number(queueMetrics?.totalWaitingCallers || 0));
  const longestWaitSeconds = useMemo(() => {
    if (waitingCallers <= 0) return 0;

    const now = Date.now() + queueDurationTick * 0;
    const startedAtMs = Date.parse(String(queueMetrics?.longestWaitStartedAt || ''));
    if (Number.isFinite(startedAtMs) && startedAtMs > 0) {
      return Math.max(0, Math.floor((now - startedAtMs) / 1000));
    }

    const baseSeconds = Math.max(0, Number(queueMetrics?.longestWaitSeconds || 0));
    const receivedAtMs = Number(queueMetrics?.metricsReceivedAt || 0);
    if (Number.isFinite(receivedAtMs) && receivedAtMs > 0) {
      return baseSeconds + Math.max(0, Math.floor((now - receivedAtMs) / 1000));
    }

    return baseSeconds;
  }, [queueDurationTick, queueMetrics?.longestWaitSeconds, queueMetrics?.longestWaitStartedAt, queueMetrics?.metricsReceivedAt, waitingCallers]);
  const longestWaitText = formatDuration(longestWaitSeconds);
  const nextCallerNumber = String(queueMetrics?.nextCallerNumber || '').trim();
  const queueStatusText = String(queueMetrics?.agentStatusLabel || '').trim() ||
    (asteriskConnected ? 'Syncing...' : 'Asterisk Offline');
  const isRecordingActive = Boolean(recordingState?.isRecording);
  const recordingDurationText = formatDuration(recordingState?.durationSeconds || 0);

  useImperativeHandle(ref, () => ({
    openDialpad: () => {
      if (!isLive && canUseDialpad && onDialpadOpen) {
        onDialpadOpen();
        return true;
      }
      return false;
    },
    toggleHold: () => {
      if (holding || (!resolveChannelId() && !onSoftphoneHoldChange)) return false;
      toggleHold();
      return true;
    },
    toggleMute: () => {
      if (!isCallActive || !onToggleMute) return false;
      return onToggleMute() !== false;
    },
    openTransfer: () => {
      return handleTransferClick();
    },
    transferToFeedback: () => {
      if (transferringToFeedback || !resolveChannelId()) return false;
      handleFeedbackTransfer();
      return true;
    },
    openSearch: () => {
      if (!hasCallerId || !onSearchHistoryOpen) return false;
      onSearchHistoryOpen();
      return true;
    },
    openList: () => {
      if (!onListOpen) return false;
      onListOpen();
      return true;
    },
    openVoicemail: () => {
      if (!onVoicemailOpen) return false;
      onVoicemailOpen();
      return true;
    },
    openProfile: () => {
      if (!onProfileOpen) return false;
      onProfileOpen();
      return true;
    },
    openReminder: () => {
      if (!onReminderOpen) return false;
      onReminderOpen();
      return true;
    },
    openHistory: () => {
      if (!hasQueryId || !onViewHistoryOpen) return false;
      onViewHistoryOpen();
      return true;
    },
    openRegistration: () => {
      if (!onSIPRegistrationOpen) return false;
      onSIPRegistrationOpen();
      return true;
    },
    openSms: () => {
      if (!onSmsOpen) return false;
      onSmsOpen();
      return true;
    },
    openNotes: () => {
      if (!onNoteOpen) return false;
      onNoteOpen();
      return true;
    },
    hangup: () => {
      if (!(isLive || currentChannel) || !onHangup) return false;
      onHangup();
      return true;
    },
    emergencyCleanup: () => {
      if (!asteriskConnected || !onEmergencyHangup) return false;
      onEmergencyHangup();
      return true;
    },
    closeTransferModal: () => {
      if (!showTransferModal) return false;
      setShowTransferModal(false);
      return true;
    }
  }), [
    asteriskConnected,
    currentChannel,
    canUseDialpad,
    handleFeedbackTransfer,
    handleTransferClick,
    hasCallerId,
    hasQueryId,
    holding,
    isCallActive,
    isLive,
    onDialpadOpen,
    onEmergencyHangup,
    onHangup,
    onListOpen,
    onVoicemailOpen,
    onNoteOpen,
    onSoftphoneHoldChange,
    onToggleMute,
    onProfileOpen,
    onReminderOpen,
    onSearchHistoryOpen,
    onSIPRegistrationOpen,
    onSmsOpen,
    onViewHistoryOpen,
    resolveChannelId,
    showTransferModal,
    toggleHold,
    transferringToFeedback
  ]);

  const ControlButton = ({ icon, label, onClick, isActive, disabled = false, title }) => (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      className={`btn d-flex flex-column align-items-center justify-content-center p-2 w-100 ${
        disabled
          ? 'btn-outline-secondary disabled'
          : isActive
          ? 'btn-primary'
          : 'btn-outline-secondary'
      }`}
      style={{
        height: '80px',
        fontSize: '0.7rem',
        fontWeight: '600',
        lineHeight: '1.2',
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}
    >
      <div className="mb-1">{icon}</div>
      <span className="text-center" style={{fontSize: '0.65rem'}}>{label}</span>
    </button>
  );

  const dialpadDisabled = isLive || !canUseDialpad;
  const dialpadTitle = !canUseDialpad && !isLive
    ? 'Switch to Outbound mode to use dialpad'
    : undefined;

  return (
    <div className="card h-100 shadow-sm border-0 d-flex flex-column leftpanel-card">
      <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0 fw-bold text-dark">Call Controls</h5>
        <span className={`badge rounded-pill ${statusColor}`}>
          {statusText}
        </span>
      </div>
      <div className="card-body d-flex flex-column p-3 leftpanel-body">

        <div className="row g-2 mb-3">
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-telephone" style={{fontSize: '1.2rem'}}></i>}
              label={isLive ? "In Call" : "Dialpad"}
              onClick={dialpadDisabled ? undefined : onDialpadOpen}
              isActive={false}
              disabled={dialpadDisabled}
              title={dialpadTitle}
            />
          </div>
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-pause-circle" style={{fontSize: '1.2rem'}}></i>}
              label={isOnHold ? "Resume" : "Hold"}
              onClick={toggleHold}
              isActive={isOnHold}
              disabled={holding || (!resolveChannelId() && !onSoftphoneHoldChange)}
            />
          </div>
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-mic-mute" style={{fontSize: '1.2rem'}}></i>}
              label="Mute"
              onClick={onToggleMute}
              isActive={isMuted}
              disabled={!isCallActive || !onToggleMute}
            />
          </div>
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-arrow-left-right" style={{fontSize: '1.2rem'}}></i>}
              label="Transfer"
              onClick={handleTransferClick}
              disabled={!isCallActive}
            />
          </div>
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-star" style={{fontSize: '1.2rem'}}></i>}
              label={transferringToFeedback ? 'Routing...' : 'Feedback'}
              onClick={handleFeedbackTransfer}
              disabled={transferringToFeedback || !resolveChannelId()}
            />
          </div>
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-search" style={{fontSize: '1.2rem'}}></i>}
              label="Search"
              onClick={onSearchHistoryOpen}
              disabled={!hasCallerId}
            />
          </div>
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-list-ul" style={{fontSize: '1.2rem'}}></i>}
              label="List"
              onClick={onListOpen}
            />
          </div>
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-voicemail" style={{fontSize: '1.2rem'}}></i>}
              label="Voicemail"
              onClick={onVoicemailOpen}
            />
          </div>
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-person-circle" style={{fontSize: '1.2rem'}}></i>}
              label="Profile"
              onClick={onProfileOpen}
            />
          </div>
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-bell" style={{fontSize: '1.2rem'}}></i>}
              label="Reminder"
              onClick={onReminderOpen}
            />
          </div>
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-clock-history" style={{fontSize: '1.2rem'}}></i>}
              label="History"
              onClick={onViewHistoryOpen}
              disabled={!hasQueryId}
            />
          </div>
          <div className="col-4 d-none">
            <ControlButton
              icon={<i className="bi bi-telephone-plus" style={{fontSize: '1.2rem'}}></i>}
              label={currentUser ? `Ext ${currentUser.extension}` : "Register"}
              onClick={onSIPRegistrationOpen}
              isActive={!!currentUser}
            />
          </div>
          <div className="col-4 d-none">
            <ControlButton
              icon={<i className="bi bi-chat-dots" style={{fontSize: '1.2rem'}}></i>}
              label="Send Whatsapp"
              onClick={onSmsOpen}
            />
          </div>
          <div className="col-4">
            <ControlButton
              icon={<i className="bi bi-journal-text" style={{fontSize: '1.2rem'}}></i>}
              label="Note"
              onClick={onNoteOpen}
            />
          </div>
        </div>

        {incomingCallContent}

        {/* Hangup Button (only show when there's an active call) */}
        {(showStandaloneHangup || isLive || currentChannel) && !hideStandaloneHangup && (
          <div className="row g-2 mb-3">
            <div className="col-12">
              <button
                onClick={onHangup}
                className="btn btn-danger w-100 d-flex flex-column align-items-center justify-content-center p-2"
                style={{
                  height: '80px',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  lineHeight: '1.2'
                }}
              >
                <div className="mb-1">
                  <i className="bi bi-telephone-x" style={{fontSize: '1.5rem'}}></i>
                </div>
                <span>Hangup Call</span>
              </button>
            </div>
          </div>
        )}

        <div className="row g-2 mb-3">
          <div className="col-6">
            <button
              onClick={() => onVideoChatOpen('justiceHub')}
              className="btn btn-info w-100 d-flex flex-column align-items-center justify-content-center p-2"
              style={{
                height: '80px',
                fontSize: '0.65rem',
                fontWeight: '600',
                lineHeight: '1.2'
              }}
            >
              <div className="mb-1">
                <i className="bi bi-camera-video" style={{fontSize: '1.2rem'}}></i>
              </div>
              <span className="text-center">Justice Hub</span>
            </button>
          </div>
          <div className="col-6">
            <button
              onClick={() => onVideoChatOpen('depd')}
              className="btn btn-warning w-100 d-flex flex-column align-items-center justify-content-center p-2"
              style={{
                height: '80px',
                fontSize: '0.65rem',
                fontWeight: '600',
                lineHeight: '1.2'
              }}
            >
              <div className="mb-1">
                <i className="bi bi-universal-access" style={{fontSize: '1.2rem'}}></i>
              </div>
              <span className="text-center">DEPD Assist</span>
            </button>
          </div>
        </div>

        <hr className="my-3" />

        {isRecordingActive && (
          <div className="alert alert-danger py-2 d-flex align-items-center">
            <span className="spinner-grow spinner-grow-sm text-danger me-2" style={{width: '0.5rem', height: '0.5rem'}}></span>
            <strong className="small">REC {recordingDurationText}</strong>
          </div>
        )}

        {/* <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="consent"
            checked={consentGiven}
            onChange={(e) => setConsentGiven(e.target.checked)}
          />
          <label className="form-check-label small" htmlFor="consent">
            Caller consent for recording has been verbally obtained.
          </label>
        </div> */}

        <div className="leftpanel-queue-summary">
          <div className="leftpanel-queue-summary-header">
            <span>Queue Monitor</span>
            <span className={`leftpanel-queue-pill ${waitingCallers > 0 ? 'leftpanel-queue-pill-hot' : ''}`}>
              {waitingCallers > 0 ? `${waitingCallers} waiting` : 'Clear'}
            </span>
          </div>
          <div className="leftpanel-queue-summary-grid">
            <div className="leftpanel-queue-summary-item">
              <span className="leftpanel-queue-summary-label">Waiting Callers</span>
              <span className="leftpanel-queue-summary-value">{waitingCallers}</span>
            </div>
            <div className="leftpanel-queue-summary-item">
              <span className="leftpanel-queue-summary-label">Longest Wait</span>
              <span className="leftpanel-queue-summary-value">{longestWaitText}</span>
            </div>
            <div className="leftpanel-queue-summary-item">
              <span className="leftpanel-queue-summary-label">Next Caller</span>
              <span className="leftpanel-queue-summary-value">{nextCallerNumber || '—'}</span>
            </div>
            <div className="leftpanel-queue-summary-item">
              <span className="leftpanel-queue-summary-label">Agent Status</span>
              <span className="leftpanel-queue-summary-value">{queueStatusText}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card-footer bg-white border-top mt-auto">
        <div className="d-grid gap-2">
          {asteriskConnected && (
            <button
              className="btn btn-warning btn-sm d-flex align-items-center justify-content-center gap-2"
              onClick={onEmergencyHangup}
              title="Emergency: Hangup all active channels"
            >
              <i className="bi bi-exclamation-triangle"></i>
              Emergency Cleanup
            </button>
          )}
        </div>
      </div>

      {/* Transfer Modal */}
      {showTransferModal && isCallActive && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Transfer Call - Available Agents</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTransferModal(false)}
                  disabled={transferring || Boolean(activeAttendedTransfer)}
                ></button>
              </div>
              <div className="modal-body">
                {transferSuccess && (
                  <div className={`alert ${transferSuccess.success ? 'alert-success' : 'alert-danger'} mb-3`}>
                    {transferSuccess.message}
                  </div>
                )}

                {loadingUsers ? (
                  <div className="text-center py-3">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Extension</th>
                          <th>Username</th>
                          <th>Status</th>
                          <th>Direction</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.length > 0 ? (
                          users.map((user, index) => (
                            <tr key={index}>
                              <td>
                                <strong>{user.extension}</strong>
                              </td>
                              <td>{user.displayName || user.username}</td>
                              <td>
                                <span className={`badge ${getUserStatusClass(user.status)}`}>
                                  {getUserStatusDisplay(user.status)}
                                </span>
                              </td>
                              <td>
                                {user.direction ? user.direction.charAt(0).toUpperCase() + user.direction.slice(1) : 'N/A'}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleUserTransfer(user.extension, user.displayName || user.username)}
                                  disabled={transferring || Boolean(activeAttendedTransfer) || !isTransferableStatus(user.status)}
                                  title={!isTransferableStatus(user.status) ? 'User not available for transfer' : 'Transfer call to this user'}
                                >
                                  {transferring ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1"></span>
                                      Transferring...
                                    </>
                                  ) : (
                                    <>
                                      <i className="bi bi-arrow-left-right me-1"></i> Transfer
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center">
                              No available agents found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {activeAttendedTransfer ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => handleAttendedTransferAction('cancel')}
                      disabled={transferring}
                    >
                      Cancel Transfer
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => handleAttendedTransferAction('complete')}
                      disabled={transferring}
                    >
                      Complete Transfer
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowTransferModal(false)}
                    disabled={transferring}
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

LeftPanelNew.displayName = 'LeftPanelNew';

export default LeftPanelNew;
